import { Chip } from '@/global/components/Chip'
import { hasPermission } from '@/hooks/useBackendMenu'
import { TipoCompensacaoManual } from '@acme/core'
import dayjs from 'dayjs'
import Dialog from 'global/guides/Dialog'
import ConfirmarExclusao from 'global/guides/Dialog/ConfirmarExclusao'
import { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import {
    CompensacaoManual,
    compensacaoManual,
} from 'service/api/endpoints/compensacao-manual'
import { toast } from 'sonner'
import FormCompensacaoManual from '../../CompensacaoManual/forms/FormCompensacaoManual'
import { useDigitacaoContext } from '../pages/useDigitacaoContext'
import { useUpdateDigitacaoQueryData } from '../pages/useDigitacaoData'
import { funcionarioPodeSolicitarAjustes } from './DetalhesRegistros'

export default function DetalhesCompensacaoManual() {
    const { mutateAsync: deleteMutateAsync, isLoading: isDeleting } =
        compensacaoManual.mutations.useDelete()
    const { mutateAsync: updateMutateAsync, isLoading: isUpdating } =
        compensacaoManual.mutations.useUpdate()
    const navigate = useNavigate()
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const { id } = useParams()
    const { data, getFuncionarioNome } = useDigitacaoContext()

    const compensacao = data.find(
        ({ compensacaoManual }) => compensacaoManual?.id === Number(id),
    )?.compensacaoManual

    const funcionarioPodeAjustar = funcionarioPodeSolicitarAjustes()
    const [excluindo, setExcluindo] = useState(false)

    if (!compensacao) return null

    const referencia = `${getFuncionarioNome(compensacao.funcionarioId ?? 0)} · ${dayjs.utc(compensacao.data).format('DD/MM/YYYY')}`

    const podeExcluir =
        hasPermission(['delete', 'CompensaçãoManual']) || funcionarioPodeAjustar

    const handleExcluir = () => {
        deleteMutateAsync(compensacao.id, {
            onSuccess: () => {
                setQueryData('COMPENSACAO_MANUAL', compensacao, true)
                toast.success('Compensação manual excluída com sucesso')
                navigate(-1)
            },
            onError: e => {
                toast.error(e.message)
            },
        })
    }

    const handleSubmit: SubmitHandler<CompensacaoManual> = input => {
        updateMutateAsync(
            {
                ...input,
                competencia: compensacao.competencia,
                id: Number(id),
            },
            {
                onSuccess: res => {
                    setQueryData('COMPENSACAO_MANUAL', res)
                    toast.success('Compensação manual atualizada com sucesso')
                    navigate(-1)
                },
                onError: err => {
                    toast.error(err.message)
                },
            },
        )
    }

    return (
        <Dialog.Root
            disableFields={!hasPermission(['update', 'CompensaçãoManual'])}
            defaultValues={{
                tipo: compensacao.tipo,
                empresaId: compensacao.empresaId,
                funcionarioId: compensacao.funcionarioId,
                data: compensacao.data,
                destinos: compensacao.Destinos?.map(d => ({
                    data: dayjs.utc(d.data).toDate(),
                })),
            }}
            onSubmit={
                hasPermission(['update', 'CompensaçãoManual'])
                    ? handleSubmit
                    : undefined
            }
            loading={isDeleting || isUpdating}
        >
            <Dialog.Title action="Detalhes da compensação manual">
                {referencia}
            </Dialog.Title>
            <Dialog.Content>
                <FormCompensacaoManual />
                <Dialog.Content.Footer>
                    {compensacao.tipo !== TipoCompensacaoManual.MESMO_DIA && (
                        <div className="flex justify-end">
                            <Chip
                                label={
                                    compensacao.calculoId
                                        ? 'Processado'
                                        : 'Não processado'
                                }
                                color={
                                    compensacao.calculoId
                                        ? 'success'
                                        : undefined
                                }
                            />
                        </div>
                    )}
                </Dialog.Content.Footer>
                <ConfirmarExclusao
                    width="2xl"
                    open={excluindo}
                    onClose={() => setExcluindo(false)}
                    loading={isDeleting}
                    onConfirm={handleExcluir}
                    title="Tem certeza que deseja excluir a compensação manual?"
                />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                {podeExcluir && (
                    <Dialog.Actions.PrimaryButton
                        color="error"
                        data-testid="dialog-delete-button"
                        onClick={() => setExcluindo(true)}
                    >
                        Excluir
                    </Dialog.Actions.PrimaryButton>
                )}
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
